import { VideoStore } from '../video.store';
import { computed } from '@preact/signals';
import { ResponsiveControlsStrategy } from './responsive-controls-strategy';
import { FullScreenControlsStrategy } from './full-screen-controls-strategy';
import { MpuControlsStrategy } from './mpu-controls-strategy';
import { isMediumRectangleAdUnit } from '../../adUnit';
import { HideControlsStrategy } from './hide-controls-strategy';
import { EndScreenCTAControlsStrategy } from './end-screen-cta-controls-strategy';
import { ConfigStore } from '../config.store';
import { ControlsStrategy } from './default-controls-strategy';

import { EndScreenType } from '../../../../formats/video-sdk3/lib/end-screen/end-screen-types';
import { EndScreenOrchestrator } from '../../../../formats/video-sdk3/lib/end-screen/orchestrator';

export class ControlsStrategyFactory {
  _responsiveStrategy: ResponsiveControlsStrategy;
  _fullScreenStrategy: FullScreenControlsStrategy;
  _mpuStrategy: MpuControlsStrategy;
  _endScreenCTAStrategy: EndScreenCTAControlsStrategy;
  _hideStrategy: HideControlsStrategy;
  _videoStore: VideoStore;
  _configStore: ConfigStore;

  adContext: Record<string, any>;

  strategy = computed<ControlsStrategy>(() => {
    const isVideoExpanded = this._videoStore.isVideoExpanded.value;
    if (this._shouldDisplayEndScreenCTAControls()) {
      return this._endScreenCTAStrategy;
    } else if (this._shouldDisplayFullScreenControls(isVideoExpanded)) {
      return this._fullScreenStrategy;
    } else if (this._shouldDisplayMPUControls()) {
      return this._mpuStrategy;
    } else if (this._shouldDisplayResponsiveControls(isVideoExpanded)) {
      return this._responsiveStrategy;
    }
    return this._hideStrategy;
  });

  constructor(
    videoStore: VideoStore,
    config: ConfigStore,
    adContext: Record<string, any>
  ) {
    this._videoStore = videoStore;
    this.adContext = adContext;
    this._configStore = config;
    this._responsiveStrategy = new ResponsiveControlsStrategy(
      videoStore,
      config,
      adContext
    );
    this._fullScreenStrategy = new FullScreenControlsStrategy(
      videoStore,
      config,
      adContext
    );
    this._mpuStrategy = new MpuControlsStrategy(videoStore, config, adContext);

    this._endScreenCTAStrategy = new EndScreenCTAControlsStrategy(
      videoStore,
      config,
      adContext
    );

    this._hideStrategy = new HideControlsStrategy(
      videoStore,
      config,
      adContext
    );
  }

  _shouldDisplayEndScreenCTAControls(): boolean {
    const endScreen = EndScreenOrchestrator.instance.endScreen;

    if (!endScreen || endScreen.type !== EndScreenType.DEFAULT) {
      return false;
    }

    return !!this._configStore.config.endScreen?.ctaOverlay;
  }

  _shouldDisplayFullScreenControls(isVideoExpanded: boolean): boolean {
    return isVideoExpanded;
  }

  _shouldDisplayResponsiveControls(isVideoExpanded: boolean): boolean {
    if (isVideoExpanded) {
      return false;
    }

    return !!this._configStore.config.controls?.layout
      ?.canDisplayResponsiveControls;
  }

  _shouldDisplayMPUControls(): boolean {
    return isMediumRectangleAdUnit(this.adContext.adUnitType);
  }
}
